// Display helpers for Access reviews (spec docs/superpowers/specs/2026-10-26-access-reviews-design.md). Words, dates and
// the reviewer link fragment. The rules (decisions, bulk, step-up, notes) live once in portal/shared/access-reviews.mjs.
import { DECISIONS } from '../../../shared/access-reviews.mjs';

const DAY = 86_400_000;

/** "Oct 26" (this year) or "Oct 26, 2027"; '' for a missing or bad date. Dates are shown in UTC, like the evidence. */
export function shortDate(iso, now = new Date()) {
  const t = Date.parse(iso ?? '');
  if (Number.isNaN(t)) return '';
  const d = new Date(t);
  const sameYear = d.getUTCFullYear() === now.getUTCFullYear();
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }), timeZone: 'UTC' });
}

/** "Oct 26, 2026" always with the year. */
export function longDate(iso) {
  const t = Date.parse(iso ?? '');
  if (Number.isNaN(t)) return '';
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** A date-only value (YYYY-MM-DD) in words: "Oct 1, 2026". */
export const dateOnlyWords = (ymd) => (typeof ymd === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ymd) ? longDate(`${ymd}T00:00:00Z`) : '');

/** "Oct 12 to Oct 26, 2026" (one year) or "Dec 14, 2026 to Jan 4, 2027". */
export function periodWords(from, to) {
  const a = Date.parse(from ?? '');
  const b = Date.parse(to ?? '');
  if (Number.isNaN(a) || Number.isNaN(b)) return longDate(from) || longDate(to);
  const sameYear = new Date(a).getUTCFullYear() === new Date(b).getUTCFullYear();
  const first = new Date(a).toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }), timeZone: 'UTC' });
  return `${first} to ${longDate(to)}`;
}

/** "6 days left", "Due today", "4 days overdue". */
export function daysLeftWords(daysLeft) {
  if (!Number.isInteger(daysLeft)) return '';
  if (daysLeft > 1) return `${daysLeft} days left`;
  if (daysLeft === 1) return '1 day left';
  if (daysLeft === 0) return 'due today';
  return `${-daysLeft} ${daysLeft === -1 ? 'day' : 'days'} overdue`;
}

/** The last sign-in in words: "Today", "Yesterday", "5 days ago", "Never". */
export function lastSignInWords(iso, now = new Date()) {
  const t = Date.parse(iso ?? '');
  if (Number.isNaN(t)) return 'Never';
  const days = Math.floor((Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - Date.UTC(new Date(t).getUTCFullYear(), new Date(t).getUTCMonth(), new Date(t).getUTCDate())) / DAY);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days} days ago`;
}

/** "1 assignment", "6 assignments". */
export const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A person or holder in words: the name, else the sign-in name, else a neutral word. */
export function personName(p, fallback = 'Someone') {
  if (!p) return fallback;
  if (typeof p === 'string') return p;
  return p.displayName || p.upn || p.address || fallback;
}

/** How a saved decision reads on a collapsed row (spec 13.2): "Kept by you, Oct 20: Needed for daily work." */
const SAVED_WORDS = Object.freeze({ keep: 'Kept by you', remove: 'Removal chosen by you', 'not-sure': 'Passed on by you with a note' });
export function savedLine(decision, now = new Date()) {
  if (!decision) return '';
  const head = SAVED_WORDS[decision.decision] ?? 'Saved by you';
  const when = shortDate(decision.decidedAt, now);
  const why = [decision.reasonLabel, decision.text].filter(Boolean).join(': ');
  return `${head}${when ? `, ${when}` : ''}${why ? `: ${why}` : ''}${/[.!?]$/.test(why) ? '' : '.'}`;
}

// ---- The reviewer link fragment ----------------------------------------------------------------------------------

// The token is 32 random bytes in base64url (43 characters, spec 7.2); the item reference is an opaque per-email id.
const TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;
const ITEM_RE = /^[A-Za-z0-9_-]{1,64}$/;

/** `#t=<token>&i=<itemRef>&d=keep|remove|not-sure`: the parts that are well formed, nothing else. */
export function readReviewFragment(hash) {
  const params = new URLSearchParams(String(hash ?? '').replace(/^#/, ''));
  const out = {};
  const t = params.get('t');
  if (t && TOKEN_RE.test(t)) out.t = t;
  const i = params.get('i');
  if (out.t && i && ITEM_RE.test(i)) out.i = i;
  const d = params.get('d');
  if (out.i && DECISIONS.includes(d)) out.d = d;
  return out;
}

/**
 * Reads the fragment once and removes it from the address bar (history.replaceState), so the token is not left in
 * the history, a bookmark or a screenshot. Like the Watch link (lib/watch-fragment.js).
 */
export function takeReviewFragment(win = window) {
  const out = readReviewFragment(win.location.hash);
  if (win.location.hash) win.history.replaceState(null, '', `${win.location.pathname}${win.location.search ?? ''}`);
  return out;
}

// ---- Admin page words ------------------------------------------------------------------------------------------------

export const REVIEWER_STATE = Object.freeze({
  done: { tone: 'good', word: 'Done' },
  'in-progress': { tone: 'info', word: 'In progress' },
  'not-started': { tone: 'off', word: 'Not started' },
  escalated: { tone: 'warn', word: 'Escalated' },
});

export const REMOVAL_STATE = Object.freeze({
  pending: { tone: 'info', word: 'Waiting for removal' },
  overdue: { tone: 'bad', word: 'Still assigned' },
  'verified-removed': { tone: 'good', word: 'Verified removed' },
  'changed-kind': { tone: 'warn', word: 'Changed kind, not removed' },
});

export const CAMPAIGN_TONE = Object.freeze({ scheduled: 'off', open: 'info', overdue: 'warn', closing: 'info', closed: 'good', cancelled: 'off' });
export const RESULT_TONE = Object.freeze({ met: 'good', 'partially-met': 'warn', 'not-met': 'bad' });

export const CANCEL_REASONS = Object.freeze([
  { id: 'started-by-mistake', label: 'Started by mistake' },
  { id: 'wrong-scope', label: 'The wrong roles were in scope' },
  { id: 'replaced', label: 'Replaced by another campaign' },
  { id: 'other', label: 'Another reason' },
]);

export const WEEKDAYS = Object.freeze(['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']);
